import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Typography, message, Spin } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import CoachStatsCard from '../components/shared/CoachStatsCard';
import { apiService } from '../../../services/api';
import { CoachStats } from '../types';

const { Title } = Typography;

interface OverallMetrics {
  totalCoaches: number;
  avgRating: number;
  avgSatisfaction: number;
  avgRetention: number;
  totalSessions: number;
  avgResponseTime: number;
  avgGoalAchievement: number;
  totalRevenue: number;
  totalActiveClients: number;
  capacityUtilization: number;
  avgExperience: number;
  avgClientLoad: number;
}

const PerformanceAnalytics: React.FC = () => {
  const [overallMetrics, setOverallMetrics] = useState<OverallMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [coachStats, setCoachStats] = useState<CoachStats | null>(null);

  const loadPerformanceData = async () => {
    setLoading(true);
    try {
      // Load coach statistics from backend
      const statsResponse = await apiService.getCoachStats();
      
      if (statsResponse.success && statsResponse.data) {
        const stats = statsResponse.data.data;
        setCoachStats(stats);
        
        // Transform backend stats to our metrics format
        const metrics: OverallMetrics = {
          totalCoaches: stats.total,
          avgRating: 4.2, // Placeholder - would need individual coach ratings
          avgSatisfaction: 87, // Placeholder - would need satisfaction data
          avgRetention: stats.averageRetentionRate,
          totalSessions: 0, // Not available in current backend
          avgResponseTime: 2.5, // Placeholder - would need response time data
          avgGoalAchievement: 85, // Placeholder - would need goal achievement data
          totalRevenue: 0, // Would need to calculate from coach performance data
          totalActiveClients: stats.totalClientsServed,
          capacityUtilization: stats.capacityUtilization,
          avgExperience: stats.averageExperience,
          avgClientLoad: stats.averageClientLoad,
        };
        
        setOverallMetrics(metrics);
      } else {
        message.error('Failed to load performance data');
      }
    } catch (error) {
      console.error('Error loading performance data:', error);
      message.error('Error loading performance data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPerformanceData();
  }, []);

  if (loading && !overallMetrics) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <BarChartOutlined /> Performance Analytics
        </Title>
        <Typography.Text type="secondary">
          Comprehensive performance analysis and coaching metrics
        </Typography.Text>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <CoachStatsCard
            title="Performance Overview"
            stats={[
              { title: 'Avg Rating', value: overallMetrics?.avgRating || 0, precision: 1 },
              { title: 'Client Satisfaction', value: overallMetrics?.avgSatisfaction || 0, precision: 1, suffix: '%' },
              { title: 'Retention Rate', value: overallMetrics?.avgRetention || 0, precision: 1, suffix: '%' },
            ]}
            loading={loading}
          />
        </Col>
        
        <Col xs={24} lg={8}>
          <CoachStatsCard
            title="Operational Metrics"
            stats={[
              { title: 'Capacity Utilization', value: overallMetrics?.capacityUtilization || 0, precision: 1, suffix: '%' },
              { title: 'Avg Experience', value: overallMetrics?.avgExperience || 0, precision: 1, suffix: 'yrs' },
              { title: 'Avg Client Load', value: overallMetrics?.avgClientLoad || 0, precision: 1 },
            ]}
            loading={loading}
          />
        </Col>
        
        <Col xs={24} lg={8}>
          <CoachStatsCard
            title="Coach Statistics"
            stats={[
              { title: 'Total Coaches', value: overallMetrics?.totalCoaches || 0 },
              { title: 'Active Coaches', value: coachStats?.active || 0 },
              { title: 'Total Clients', value: overallMetrics?.totalActiveClients || 0 },
            ]}
            loading={loading}
          />
        </Col>

        <Col xs={24}>
          <Card title="Specializations Distribution" loading={loading}>
            {coachStats?.bySpecialization ? (
              <div style={{ padding: '20px' }}>
                <Row gutter={[16, 16]}>
                  {Object.entries(coachStats.bySpecialization).map(([spec, count]) => (
                    <Col xs={12} sm={8} md={6} lg={4} key={spec}>
                      <Card size="small" style={{ textAlign: 'center' }}>
                        <Typography.Text strong>{count}</Typography.Text>
                        <br />
                        <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                          {spec}
                        </Typography.Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            ) : (
              <div style={{ 
                height: 200, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#fafafa',
                border: '1px dashed #d9d9d9',
                borderRadius: '6px'
              }}>
                <Typography.Text type="secondary">
                  No specialization data available
                </Typography.Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Performance Charts">
            <div style={{ 
              height: 300, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: '#fafafa',
              border: '1px dashed #d9d9d9',
              borderRadius: '6px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <Title level={4} type="secondary">
                  Performance Charts Placeholder
                </Title>
                <Typography.Text type="secondary">
                  Advanced charts will be available when more performance data is collected
                </Typography.Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PerformanceAnalytics; 